import React, { useState, useEffect } from "react";
import { getFirestore, collection, getDocs, doc, setDoc, updateDoc, deleteDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { getAuth, createUserWithEmailAndPassword, updatePassword } from "firebase/auth";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { arrayUnion } from "firebase/firestore";
import "./AdminRoles.css";

const CustomContent = () => {
  const [customContent, setCustomContent] = useState([]);
  const [showDetailsPopup, setShowDetailsPopup] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null }); // State for sorting
  const [noteText, setNoteText] = useState("");
  const [file, setFile] = useState(null);
  const navigate = useNavigate();
  const db = getFirestore();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const customContentCollection = collection(db, "customContent");
        const customContentSnapshot = await getDocs(customContentCollection);
        const contentList = customContentSnapshot.docs.map((doc) => ({
          id: doc.id, // Fetch the Document ID
          ...doc.data(),
        }));
        setCustomContent(contentList);
      } catch (error) {
        console.error("Error fetching data:", error.message);
      }
    };

    fetchData();
  }, [db]);

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });

    const sortedContent = [...customContent].sort((a, b) => {
      if (a[key] < b[key]) return direction === "asc" ? -1 : 1;
      if (a[key] > b[key]) return direction === "asc" ? 1 : -1;
      return 0;
    });
    setCustomContent(sortedContent);
  };

  const togglePaidStatus = async (contentId, currentStatus) => {
    try {
      const contentRef = doc(db, "customContent", contentId);
      await updateDoc(contentRef, { paid: !currentStatus });

      setCustomContent((prev) =>
        prev.map((content) =>
          content.id === contentId ? { ...content, paid: !currentStatus } : content
        )
      );
    } catch (error) {
      console.error("Error updating paid status:", error.message);
      alert("Failed to update paid status. Please try again.");
    }
  };

  const handleDeleteContent = async (contentId) => {
    try {
      const contentRef = doc(db, "customContent", contentId);
      await deleteDoc(contentRef);

      setCustomContent((prev) => prev.filter((content) => content.id !== contentId));
      setShowDetailsPopup(null);
      alert("Content deleted successfully!");
    } catch (error) {
      console.error("Error deleting content:", error.message);
      alert("Failed to delete content. Please try again.");
    }
  };

  const handleAddNote = async (contentId, noteText, file) => {
    try {
      const contentRef = doc(db, "customContent", contentId);
      const note = { text: noteText, fileUrl: null };

      if (file) {
        const storage = getStorage();
        const fileRef = ref(storage, `notes/${contentId}/${file.name}`);
        await uploadBytes(fileRef, file);
        note.fileUrl = await getDownloadURL(fileRef);
      }

      await updateDoc(contentRef, {
        notes: arrayUnion(note),
      });

      setCustomContent((prev) =>
        prev.map((content) =>
          content.id === contentId
            ? { ...content, notes: [...(content.notes || []), note] }
            : content
        )
      );

      // Clear note fields after saving
      setNoteText("");
      setFile(null);
      alert("Note added successfully!");
    } catch (error) {
      console.error("Error adding note:", error.message);
      alert("Failed to add note. Please try again.");
    }
  };

  const handleCancelNote = () => {
    // Clear note fields when canceled
    setNoteText("");
    setFile(null);
  };

  return (
    <div className="container">
      <button onClick={() => navigate("/admin")} className="return-button">
        Return to Admin Dashboard
      </button>
      <h1>Admin Dashboard: Custom Content</h1>

      {/* Custom Content Section */}
      <div className="section">
        <h2>Custom Content</h2>
        <div className="bordered-container">
          <table className="excel-table">
            <thead>
              <tr>
                <th>ID</th> {/* New ID column */}
                {["name", "email", "grade", "state", "subject", "style", "paid"].map((key) => (
                  <th key={key}>
                    {key.charAt(0).toUpperCase() + key.slice(1)}{" "}
                    <button className="sort-button" onClick={() => handleSort(key)}>
                      {sortConfig.key === key ? (sortConfig.direction === "asc" ? "↑" : "↓") : "⇅"}
                    </button>
                  </th>
                ))}
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {customContent.map((content) => (
                <tr
                  key={content.id}
                  style={{ backgroundColor: content.paid ? "#d4edda" : "transparent" }} // Green background for paid rows
                >
                  <td>{content.id}</td>
                  <td>{content.sirName} {content.firstName} {content.lastName}</td>
                  <td>{content.email}</td>
                  <td>{content.grade}</td>
                  <td>{content.state}</td>
                  <td>{content.subject}</td>
                  <td>{content.style}</td>
                  <td>
                    <button
                      onClick={() => togglePaidStatus(content.id, content.paid)}
                      className={`toggle-button ${content.paid ? "paid" : "not-paid"}`}
                    >
                      {content.paid ? "Paid" : "Not Paid"}
                    </button>
                  </td>
                  <td>
                    <button
                      onClick={() => setShowDetailsPopup(content)}
                      className="details-button"
                    >
                      Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Details Popup */}
      {showDetailsPopup && (
        <div className="fullscreen-modal">
          <div className="fullscreen-content">
            <h2 className="center-text">Custom Content Details</h2>
            <div className="modal-content-scroll form-grid">
              {Object.entries(showDetailsPopup).map(([key, value]) => (
                <div className="form-field" key={key}>
                  <label>{key.charAt(0).toUpperCase() + key.slice(1)}:</label>
                  <span>{typeof value === "object" ? JSON.stringify(value) : value}</span>
                </div>
              ))}
              {showDetailsPopup.notes && (
                <div className="form-field">
                  <label>Notes:</label>
                  <ul>
                    {showDetailsPopup.notes.map((note, index) => (
                      <li key={index}>
                        <p>{note.text}</p>
                        {note.fileUrl && (
                          <a href={note.fileUrl} target="_blank" rel="noopener noreferrer">
                            View File
                          </a>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <div className="button-group">
              <button
                onClick={() => handleDeleteContent(showDetailsPopup.id)}
                className="red-button"
              >
                Delete
              </button>
              <button
                onClick={() => setShowDetailsPopup(null)}
                className="azul-button"
              >
                Close
              </button>
            </div>
            <div className="add-note-section">
              <h3>Add Note</h3>
              <textarea
                placeholder="Enter your note here"
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                className="textarea"
              />
              <input
                type="file"
                onChange={(e) => setFile(e.target.files[0])}
                className="file-input"
              />
              <div className="button-group">
                <button
                  onClick={() => handleAddNote(showDetailsPopup.id, noteText, file)}
                  className="green-button"
                >
                  Save
                </button>
                <button
                  onClick={handleCancelNote}
                  className="red-button"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomContent;


